import {
  useState,
  useRef,
  useEffect,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';

/**
 * `useStateRef` can be used in place of `useState` when you need both a
 * state variable and a ref to stay in sync. This is most important when you
 * have an event listener or other closure that you need to keep track of;
 * a state variable will have a stale value in this case, while a ref
 * will have access to the current value. Having both is necessary when
 * the UI depends on the variable, as changing the state will re-render while
 * updating a ref will not.
 */
function useStateRef<Type>(
  initialValue: Type
): [Type, Dispatch<SetStateAction<Type>>, MutableRefObject<Type>] {
  const [value, setValue] = useState<Type>(initialValue);

  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return [value, setValue, ref];
}

export default useStateRef;
